/**
 * Gomoku Cartridge Implementation
 */
import { GomokuEngine, PLAYER_BLACK, PLAYER_WHITE, EMPTY } from './engine.js';
import { GomokuAI } from './ai.js';
import { GomokuRenderer } from './renderer.js';

export const Cartridge = {
  id: 'gomoku',
  name: 'Gomoku',
  tagline: 'Classic Five-in-a-Row strategy duel',
  icon: '⚫⚪',
  category: 'Strategy',
  minPlayers: 1,
  maxPlayers: 2,
  modes: ['solo', 'multiplayer'],

  defaultConfig: {
    difficulty: 'medium'
  },

  configOptions: [
    {
      key: 'difficulty',
      label: 'AI Difficulty',
      type: 'select',
      options: [
        { value: 'easy', label: 'Casual (Novice)' },
        { value: 'medium', label: 'Balanced (Skilled)' },
        { value: 'hard', label: 'Master (Minimax)' }
      ],
      soloOnly: true
    }
  ],

  async init(mountElement, context) {
    this.context = context;
    this.engine = new GomokuEngine();
    this.ai = new GomokuAI(context.config?.difficulty || 'medium');
    this.mountElement = mountElement;

    // Determine colors
    // In solo: player is Black (moves first), AI is White
    // In multiplayer: Host is Black, Guest is White
    this.playerColor = context.isHost !== false ? PLAYER_BLACK : PLAYER_WHITE;
    this.opponentColor = this.playerColor === PLAYER_BLACK ? PLAYER_WHITE : PLAYER_BLACK;

    this.buildDOM();
    this.renderer = new GomokuRenderer(this.canvas, this.handleUserMoveRequest.bind(this));
    this.renderer.resize();

    window.addEventListener('resize', this.boundResize = () => {
      if (this.renderer) {
        this.renderer.resize();
        this.render();
      }
    });

    this.render();
  },

  buildDOM() {
    this.mountElement.innerHTML = `
      <div class="gomoku-wrapper" style="display:flex; flex-direction:column; align-items:center; width:100%; max-width:540px; margin:0 auto;">
        <div class="game-hud">
          <div class="hud-player">
            <span>${this.playerColor === PLAYER_BLACK ? '⚫' : '⚪'} You (${this.playerColor === PLAYER_BLACK ? 'Black' : 'White'})</span>
          </div>
          <div id="gomoku-turn-badge" class="hud-turn-indicator active">Your Turn</div>
          <div class="hud-player">
            <span>${this.opponentColor === PLAYER_BLACK ? '⚫' : '⚪'} ${this.context.mode === 'solo' ? 'Computer' : this.context.opponentName || 'Opponent'}</span>
          </div>
        </div>

        <div style="position:relative; width:100%; display:flex; justify-content:center;">
          <canvas id="gomoku-canvas" style="touch-action:none; max-width:100%; border-radius:16px;"></canvas>
        </div>

        <div id="gomoku-controls" style="margin-top:14px; width:100%; display:flex; gap:10px; justify-content:center;">
          <button id="gomoku-confirm-btn" class="btn btn-primary btn-block" style="display:none;">
            ✓ Confirm Move
          </button>
        </div>
      </div>
    `;

    this.canvas = this.mountElement.querySelector('#gomoku-canvas');
    this.turnBadge = this.mountElement.querySelector('#gomoku-turn-badge');
    this.confirmBtn = this.mountElement.querySelector('#gomoku-confirm-btn');

    this.confirmBtn.addEventListener('click', () => {
      if (this.renderer) {
        this.renderer.confirmCurrentSelection();
      }
    });
  },

  handleUserMoveRequest(x, y, isPreview = false) {
    if (this.engine.winner || this.engine.isDraw) return;
    if (this.engine.currentTurn !== this.playerColor) {
      this.context.audio?.playSfx('error');
      return;
    }

    if (isPreview) {
      this.context.audio?.playSfx('tap');
      this.confirmBtn.style.display = 'block';
      this.confirmBtn.textContent = `✓ Confirm Move (${String.fromCharCode(65 + x)}${y + 1})`;
      this.render();
      return;
    }

    // Commit Move
    this.confirmBtn.style.display = 'none';
    const moved = this.engine.makeMove(x, y, this.playerColor);
    if (!moved) return;

    this.context.audio?.playSfx('place');
    this.render();

    // Check game over
    if (this.checkGameOver()) return;

    // Dispatch remote action if multiplayer
    if (this.context.mode === 'multiplayer') {
      this.context.sendAction('move', { x, y });
    } else if (this.context.mode === 'solo') {
      this.triggerAIMove();
    }
  },

  triggerAIMove() {
    this.updateHUD();
    setTimeout(() => {
      if (this.engine.winner || this.engine.isDraw) return;
      const best = this.ai.findBestMove(this.engine, this.opponentColor);
      if (best) {
        this.engine.makeMove(best.x, best.y, this.opponentColor);
        this.context.audio?.playSfx('place');
        this.render();
        this.checkGameOver();
      }
    }, 380);
  },

  handleRemoteAction(action) {
    if (action.actionType === 'move') {
      const { x, y } = action.payload;
      const moved = this.engine.makeMove(x, y, this.opponentColor);
      if (moved) {
        this.context.audio?.playSfx('place');
        this.render();
        this.checkGameOver();
      }
    }
  },

  checkGameOver() {
    if (this.engine.winner) {
      const isWin = this.engine.winner === this.playerColor;
      this.context.audio?.playSfx(isWin ? 'victory' : 'defeat');
      this.context.onGameOver?.({
        winnerId: isWin ? this.context.playerId : this.context.opponentId,
        isDraw: false,
        reason: isWin ? 'You achieved Five in a Row!' : 'Opponent achieved Five in a Row!',
        stats: { turns: this.engine.moveHistory.length }
      });
      this.updateHUD();
      return true;
    }

    if (this.engine.isDraw) {
      this.context.audio?.playSfx('tap');
      this.context.onGameOver?.({
        winnerId: null,
        isDraw: true,
        reason: 'Board is completely filled. Game ended in a draw.',
        stats: { turns: this.engine.moveHistory.length }
      });
      this.updateHUD();
      return true;
    }

    this.updateHUD();
    return false;
  },

  updateHUD() {
    if (!this.turnBadge) return;

    if (this.engine.winner) {
      const isWin = this.engine.winner === this.playerColor;
      this.turnBadge.textContent = isWin ? 'Victory!' : 'Defeat!';
      this.turnBadge.className = isWin ? 'hud-turn-indicator active' : 'hud-turn-indicator waiting';
      return;
    }

    if (this.engine.isDraw) {
      this.turnBadge.textContent = 'Draw Game';
      this.turnBadge.className = 'hud-turn-indicator waiting';
      return;
    }

    const isMyTurn = this.engine.currentTurn === this.playerColor;
    if (isMyTurn) {
      this.turnBadge.textContent = 'Your Turn';
      this.turnBadge.className = 'hud-turn-indicator active';
    } else {
      this.turnBadge.textContent = this.context.mode === 'solo' ? 'AI Thinking...' : 'Opponent Turn';
      this.turnBadge.className = 'hud-turn-indicator waiting';
    }
  },

  render() {
    if (this.renderer) {
      this.renderer.render(this.engine, this.context.playerId, this.playerColor);
    }
    this.updateHUD();
  },

  serializeState() {
    return {
      engine: this.engine.serialize(),
      playerColor: this.playerColor,
      opponentColor: this.opponentColor
    };
  },

  restoreState(state) {
    if (state?.engine) {
      this.engine.restore(state.engine);
      this.render();
    }
  },

  pause() {},
  resume() {},

  destroy() {
    if (this.renderer) {
      this.renderer.destroy();
    }
    if (this.boundResize) {
      window.removeEventListener('resize', this.boundResize);
    }
  }
};
